import { provideHttpClient, withInterceptorsFromDi, HttpHeaders } from '@angular/common/http';
import { EnvironmentProviders, inject, Provider } from '@angular/core';
import { provideApollo } from 'apollo-angular';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { ApolloLink, DefaultOptions, Observable } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import { buildCache } from './graphql-cache';
import { getApiUrl } from './region';
import { environment } from '../../environments/environment';
import { SessionService } from './services/session.service';
import type { SessionRefreshOutcome } from './services/session-refresh';
import { refreshUnavailableError, sessionExpiredError } from './services/apollo-error';
import {
  LOGIN_OPERATIONS,
  hasUnauthorizedGraphQLError,
  httpStatusOf,
  isBackgroundOperation,
  shouldLogoutAfterAuthFailure,
  shouldMarkOffline,
  shouldRefreshOnHttp401,
} from './services/graphql-auth-helpers';
import { isAdminPreviewSession } from './guards/admin-preview';
import { RETIRED_GALLERY_PATHS } from './guards/paper-docs-redirect';

/**
 * GraphQL / Apollo provider — SLIM COOKIE-ONLY PORT.
 *
 * The employee app's provider is a large auth/recovery machine: Bearer-vs-cookie
 * surface detection (Teams / Outlook add-in / vendor portal), WebSocket
 * subscriptions, Automatic Persisted Queries, IndexedDB cache persistence and a
 * Tauri cookie-jar fetch path. NONE of that belongs in workplace-settings:
 *
 *   • It is a web PWA served same-origin — the HTTP-only session cookie is the
 *     ONLY credential. `withCredentials: true` carries it; there is no Bearer to
 *     attach and therefore no stale-Bearer 401 to recover from.
 *   • No Teams / Outlook / vendor / Tauri surfaces exist here.
 *   • No realtime subscriptions and no offline cache persistence (yet).
 *
 * WHAT THIS PORT DOES KEEP is the session-recovery ErrorLink (bd 2dv). A 401 on
 * a live session — the cookie went stale mid-flight — must refresh the cookie
 * once and replay the operation, and only end the session (redirect to
 * login-web) when that fails on a request the USER actually made. Background
 * polls degrade quietly. This is the cookie-only slice of the employee app's
 * `withTokenFactory`, reusing the same pure decision helpers
 * (`graphql-auth-helpers.ts`) so the two cannot drift.
 *
 * SECURITY: no token is read from or written to storage here. The credential is
 * the browser's HTTP-only cookie, invisible to JS by design.
 */

// Read from the environment so a rotated NG_APP_SPACES_CLIENT_ID applies
// everywhere — no hardcoded divergence.
const SPACES_CLIENT = environment.SPACES_CLIENT_ID;

const loginOperations = LOGIN_OPERATIONS;

/**
 * The per-request default headers. Pure — takes the stored tenant as an
 * argument and returns a plain object — so it is trivial to assert.
 */
export function defaultRequestHeaders(input: { tenant: string | null }): Record<string, string> {
  const headers: Record<string, string> = { spaces_client: SPACES_CLIENT };
  if (input.tenant) {
    headers['spaces_tenant'] = input.tenant;
  }
  return headers;
}

function makeDefaultsLink(): ApolloLink {
  return new SetContextLink((prevContext) => {
    let headers = (prevContext?.headers as HttpHeaders | undefined) ?? new HttpHeaders();
    const next = defaultRequestHeaders({
      tenant: localStorage.getItem('tenant'),
    });
    for (const [name, value] of Object.entries(next)) {
      headers = headers.set(name, value);
    }
    return { headers };
  });
}

/**
 * Renew the cookie credential and report which of the three things happened.
 *
 * Deliberately side-effect-FREE on failure: the logout decision belongs to the
 * caller, because it depends on whether THAT caller was user-initiated or a
 * background poll. Coalescing lives in `SessionService.refreshSession()` so the
 * route guards' refresh and the link chain's refresh share one in-flight
 * request.
 */
async function renewCredential(session: SessionService): Promise<SessionRefreshOutcome> {
  try {
    return await session.refreshSession();
  } catch {
    return 'unavailable';
  }
}

/**
 * Refresh the cookie session once, then replay the operation. Emits a terminal
 * coded UNAUTHORIZED (not a bare Error) when refresh fails so the data layer
 * treats it as an expected session expiry rather than a crash.
 *
 * `background` (a poll / prefetch / stale-while-revalidate refresh) changes ONLY
 * the failure tail: the refresh is still attempted, but when it fails the
 * session is left intact — a request the user never made must not sign them out.
 */
function retryAfterRefresh(
  operation: ApolloLink.Operation,
  forward: (op: ApolloLink.Operation) => Observable<ApolloLink.Result>,
  session: SessionService,
  logout?: () => Promise<void>,
  background = false,
): Observable<ApolloLink.Result> {
  return new Observable<ApolloLink.Result>((observer) => {
    renewCredential(session)
      .then((outcome) => {
        if (outcome !== 'refreshed') {
          const expired = outcome === 'expired';
          if (expired && !background) void logout?.();
          observer.error(expired ? sessionExpiredError() : refreshUnavailableError());
          return;
        }
        operation.setContext((ctx: Record<string, unknown>) => ({
          ...ctx,
          __retriedAfterRefresh: true,
        }));
        // NOTE: Apollo's ErrorLink subscribes the observable its handler returns
        // DIRECTLY to the caller — a retried operation never re-enters the
        // handler, so the replay's own outcome has to be judged here.
        forward(operation).subscribe({
          next: (result) => {
            if (!background && hasUnauthorizedGraphQLError(result)) void logout?.();
            observer.next(result);
          },
          error: (replayError: unknown) => {
            if (httpStatusOf(replayError) === 401) {
              if (!background) void logout?.();
              observer.error(sessionExpiredError());
              return;
            }
            observer.error(replayError);
          },
          complete: observer.complete.bind(observer),
        });
      })
      .catch((err) => observer.error(err));
  });
}

/**
 * Marks the session ONLINE the moment any operation completes without a network
 * error — the counterpart to the ErrorLink's `markOffline()`, so a recovered API
 * dismisses the connection banner on the first successful round trip.
 */
function makeOnlineProbe(session: SessionService): ApolloLink {
  return new ApolloLink((operation, forward) => {
    return new Observable<ApolloLink.Result>((observer) => {
      const sub = forward(operation).subscribe({
        next: (result) => {
          session.markOnline();
          observer.next(result);
        },
        error: (err) => observer.error(err),
        complete: () => observer.complete(),
      });
      return () => sub.unsubscribe();
    });
  });
}

/**
 * Build the auth/recovery ErrorLink (cookie-only). Exported so a spec can drive
 * the real link with a stub session and assert the background-vs-user-initiated
 * 401 behaviour end to end.
 */
export function withTokenFactory(session: SessionService, logout?: () => Promise<void>) {
  return new ErrorLink(({ error, operation, forward }) => {
    const opName = operation.operationName?.toLowerCase?.() ?? '';
    const opContext = operation.getContext() as Record<string, unknown> | undefined;
    const alreadyRetried = !!opContext?.['__retriedAfterRefresh'];
    // A poll / prefetch / silent revalidation the user never asked for. It may
    // still refresh the cookie, but it must never destroy the session.
    const background = isBackgroundOperation(opContext, opName);
    const mayLogout = shouldLogoutAfterAuthFailure(opContext, opName);

    // ── GraphQL errors (server returned a parseable response) ──
    if (CombinedGraphQLErrors.is(error)) {
      // The HTTP request actually completed — server is up.
      session.markOnline();
      for (const err of error.errors) {
        if ((err as { extensions?: { code?: unknown } }).extensions?.code === 'UNAUTHORIZED') {
          if (opName && loginOperations.includes(opName)) return undefined;
          if (alreadyRetried) {
            // Refresh + replay already failed. Only a user-initiated request
            // may end the session; a background one degrades quietly.
            if (mayLogout) void logout?.();
            return undefined;
          }
          return retryAfterRefresh(operation, forward, session, logout, background);
        }
      }
      return undefined;
    }

    // ── Bare HTTP 401 (no GraphQL body) — the cookie was rejected at the auth
    //    layer before the resolver ran. The server DID respond (so we're
    //    online); refresh the cookie + replay once, exactly like the GraphQL
    //    UNAUTHORIZED path. ──
    const status = httpStatusOf(error);
    if (status === 401) {
      session.markOnline();
      if (shouldRefreshOnHttp401(status, opName, alreadyRetried)) {
        return retryAfterRefresh(operation, forward, session, logout, background);
      }
      // Already retried once (or a login op) → expire the session cleanly, but
      // ONLY for a request the user actually made.
      if (alreadyRetried) {
        if (mayLogout) void logout?.();
        return undefined;
      }
    }

    // ── Definite HTTP responses that aren't handled above ──
    // Any status at all means the server was reached: a GraphQL-validation 400
    // (schema skew), a 403, a 500 with a body… none of these are an outage.
    if (!shouldMarkOffline(status)) {
      session.markOnline();
      return undefined;
    }

    // ── Network errors (request never reached the server) or gateway-level
    //    502/503/504 — keep the session intact and surface the offline state
    //    so a dev API restart never logs the user out. ──
    session.markOffline();
    return undefined;
  });
}

function createApollo(httpLink: HttpLink) {
  const session = inject(SessionService);

  // End an expired session (clear local state + hand off to login-web), but
  // NEVER from a surface that intentionally runs without a user session: a 401
  // there is the expected state, and bouncing to login would defeat it. Path-
  // based (workplace-settings routes are path-routed, not hash-routed).
  const logout = async () => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      // Dev-only admin preview renders the shell off the live API with whatever
      // session the developer has; its reads may 401 by design, so bouncing to
      // login would defeat the `?adminPreview=1` bypass entirely.
      if (isAdminPreviewSession()) return;
      // Public / self-contained surfaces that do not require a user session.
      // The old design-system gallery URL: a hop that leaves for the Ricoh
      // Paper docs site (guards/paper-docs-redirect.ts).
      if (RETIRED_GALLERY_PATHS.some((retired) => path === `/${retired}`)) return;
      if (path.startsWith('/preview/')) return;
      if (path.startsWith('/signage')) return;
      // The visitor's own page: a visitor has no session, the link key is the credential.
      if (path.startsWith('/v/')) return;
      // The screen that exists BECAUSE no session could be established — a 401
      // here is not news, and acting on it restarts the cycle it ended.
      if (path.startsWith('/sign-in-problem')) return;
    }
    // Counted, not unconditional: a 401 the user's own request earned ends the
    // session, but a browser already round-tripped to login-web and back to the
    // same 401 stops with an explanation instead (see auth-recovery.ts).
    await session.endExpiredSession();
  };

  // Same-origin cookie auth: the browser attaches the HTTP-only session cookie
  // when `withCredentials` is set. `uri` is a function for parity with the
  // employee app's request-time region resolution — here it returns the
  // configured endpoint unchanged.
  const http = httpLink.create({
    uri: () => getApiUrl(),
    withCredentials: true,
  });

  const link = ApolloLink.from([
    makeDefaultsLink(),
    withTokenFactory(session, logout),
    makeOnlineProbe(session),
    http,
  ]);

  const defaultOptions = {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  } satisfies DefaultOptions;

  return {
    // `buildCache()`, not a bare `new InMemoryCache()`. The typePolicies in
    // core/graphql-cache.ts had been written, commented and unit-tested and
    // then never imported by anything, so the client ran with Apollo's
    // defaults: no normalisation for any `_id`-keyed type (an edit in one
    // surface did not update the same entity in another), and none of the
    // `merge: true` policies that stop Apollo DISCARDING fields when two
    // queries return different subsets of the same object.
    cache: buildCache(),
    link,
    defaultOptions,
  };
}

export function provideGraphQL(): Array<Provider | EnvironmentProviders> {
  return [
    provideHttpClient(withInterceptorsFromDi()),
    provideApollo(() => {
      const httpLink = inject(HttpLink);
      return createApollo(httpLink);
    }),
  ];
}
